import { vars } from "./vars";
import { settings } from "./settings";
import { DateTime } from 'luxon';

/**
 * API calls are mapped in this class. All method calls are cached
 * unless explicitly added to the 'skip' variable inside the prototype
 * forEach loop. API arguments should be passed as the 'data' parameter.
 * Calls can be forced to refetch by including 'force: true' as an object
 * property in the second argument.
 */
class apiDefinition {
    constructor() {
        const calendarElement = document.querySelector('omnicms-calendar[data-calendar-id]');
        this.cache = new Cache();
        this.urlBase = settings.urlBase;
        this.calId = calendarElement.getAttribute('data-calendar-id');
        this.categories = calendarElement.getAttribute('data-categories');
        this.categories = this.categories ? this.categories.split(',') : [];
        this.widgetOffsetTop = calendarElement.getBoundingClientRect().top;
        this.originalPageTitle = document.title;
        this.originalPageDescription = document.querySelector('head meta[name="description" i]')?.getAttribute('content') ?? '';
        this.originalPageCanonical = document.querySelector('head link[rel="canonical"]')?.getAttribute('href') ?? '';
    }

    pubcalendar(data = {}, options = {}) {
        return this.fetch(
            `${this.urlBase}/pubcalendar/${this.calId}`,
            {
                data,
                ...options
            }
        );
    }

    getEvents(data, options = {}) {
        return this.fetch(
            `${this.urlBase}/pubcalendar/${this.calId}/events`,
            {
                data: {
                    ...data,
                    category: this.categories,
                },
                ...options
            }
        );
    }

    getFeaturedSliderEvents(data, options = {}) {
        if(!vars.pubcalendar.featuredSlider) return Promise.resolve([]); // If featured slider option is turned off return promise with empty array
        const date = DateTime.now();
        return this.fetch(
            `${this.urlBase}/pubcalendar/${this.calId}/events`,
            {
                data: {
                    start: date.toISODate(),
                    end: date.plus({ years: 2 }).toISODate(),
                    category: this.categories,
                    count: 50,
                    featured: true,
                },
            }
        );
    }

    getEvent(data, options = {}) {
        if(data.instanceId) return this.getEventInstance(...arguments);
        return this.fetch(
            `${this.urlBase}/pubcalendar/${this.calId}/events/${data.id}`,
            {
                ...options
            }
        );
    }

    getEventInstance(data, options = {}) {
        return this.fetch(
            `${this.urlBase}/pubcalendar/${this.calId}/events/${data.id}/instances/${data.instanceId}`,
            {
                ...options
            }
        );
    }

    getTags(data = {}, options = {}) {
        data.category = this.categories; // always include category filter
        return this.fetch(
            `${this.urlBase}/pubcalendar/${this.calId}/tags`,
            {
                data,
                ...options
            }
        );
    }

    getCategories() {
        return this.fetch(`${this.urlBase}/pubcalendar/${this.calId}/categories`)
        .then(res => {
            if(!this.categories.length) return res.sort(byName);
            return res.filter(c => this.categories.includes(c.id)).sort(byName); // only include categories configured in the embed code
        });
        
        // sort function
        function byName(a, b) {
            const nameA = a.name.toLowerCase();
            const nameB = b.name.toLowerCase();
            if(nameA < nameB) return -1;
            if(nameA > nameB) return 1;
            return 0;
        }
    }

    submitEvent(data) {
        const endpoint = `${this.urlBase}/pubcalendar/${this.calId}/submit-event`;
        
        const options = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        };

        return this.fetch(endpoint, options);
    }

    verifyPasscode(data) {
        const endpoint = `${this.urlBase}/pubcalendar/${this.calId}/submit-verify`;
        
        const options = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams(data),
        };

        return this.fetch(endpoint, options);
    }

    submitRSVP(data) {
        const endpoint = `${this.urlBase}/pubcalendar/${this.calId}/events/${data.eventId}/register`;
        
        delete data.eventId; // not needed in payload
        
        const options = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams(data),
        };

        return this.fetch(endpoint, options);
    }

    confirmAttendee(data) {
        const endpoint = `${this.urlBase}/pubcalendar/${this.calId}/events/${data['event-id']}/confirm`;

        data['calendar-id'] = this.calId;
        
        const options = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams(data),
        };

        return this.fetch(endpoint, options);
    }

    getCalendarICS(triggerDownload = true) {
        const options = {
            category: this.categories,
            url: this.getURL(),
            hash: !vars.pubcalendar.basepath,
        };
        if(triggerDownload) options.view = false;
        const requestURL = this.makeQueryString(`${this.urlBase}/pubcalendar/${this.calId}/ics`, options);
        if(triggerDownload) this.triggerICSDownload(requestURL);
        return requestURL;
    }

    getEventICS(data, triggerDownload = true) {
        const options = {
            url: this.getURL(),
            hash: !vars.pubcalendar.basepath,
        };
        if(triggerDownload) options.view = false;
        const requestURL = this.makeQueryString(`${this.urlBase}/pubcalendar/${this.calId}/events/${data.id}/ics`, options);
        if(triggerDownload) this.triggerICSDownload(requestURL);
        return requestURL;
    }

    triggerICSDownload(requestURL) {
        vars.viewContainer.querySelectorAll('.ics-iframe').forEach(e => e.remove());
        const iframe = document.createElement('iframe');
        iframe.src = requestURL;
        iframe.classList.add('ics-iframe','hide');
        vars.viewContainer.append(iframe);
    }

    getRSSURL(filterOptions = {}) {
        return this.makeQueryString(
            `${this.urlBase}/pubcalendar/${this.calId}/rss`,
            {
                category: this.categories,
                url: this.getURL(),
                hash: !vars.pubcalendar.basepath,
                tag: filterOptions.tag ?? [],
            }
        );
            
    }

    getURL() {
        return window.location.protocol + '//' + window.location.host + (vars.pubcalendar.basepath ?? window.location.pathname);
    }

    makeQueryString(urlBase, data = {}) {
        const newUrl = new URL(urlBase);

        // Append paramters to url as query strings
        Object.keys(data).forEach(key => {
            // Encode array values as multiple copies of the same key passing different values
            if (Array.isArray(data[key])) {
                data[key].forEach(value => {
                    newUrl.searchParams.append(key, value);
                })
            } else {
                // Encode normally
                newUrl.searchParams.append(key, data[key]);
            }
        });
        
        return newUrl;
    }

    setPageTitle(pageTitle) {
        if(pageTitle) {
            document.title = `${pageTitle} - ${this.originalPageTitle}`;
        } else {
            document.title = this.originalPageTitle;
        }
    }

    setPageDescription(pageDescription) {
        const metaDescriptionTag = document.querySelector('head meta[name="description" i]');
        if(!metaDescriptionTag) return;
        
        if(pageDescription) {
            const temporalElement = document.createElement('div');
            temporalElement.innerHTML = pageDescription;
            const pageDescriptionText = temporalElement.textContent;
            metaDescriptionTag.setAttribute('content', pageDescriptionText);
        } else {
            metaDescriptionTag.setAttribute('content', this.originalPageDescription);
        }
    }

    setPageCanonical(canonicalURL) {
        const canonicalTag = document.querySelector('head link[rel="canonical"]');
        if(!canonicalTag) return;

        if(canonicalURL) {
            canonicalTag.setAttribute('href', canonicalURL);
        } else {
            canonicalTag.setAttribute('href', this.originalPageCanonical);
        }
    }

    adjustScrollPosition() {
        // if the widget is below the fold scroll into viewport
        if(window.innerHeight < this.widgetOffsetTop) {
           window.scrollTo(0, this.widgetOffsetTop);
        }
    }

    hexToRGB(hex) {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex) ?? /^#?([a-f\d]{1})([a-f\d]{1})([a-f\d]{1})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1].repeat(2).substring(0, 2), 16),
            g: parseInt(result[2].repeat(2).substring(0, 2), 16),
            b: parseInt(result[3].repeat(2).substring(0, 2), 16),
        } : null;
    }

    fetch(url, options = {}) {
        const newOptions = {
            method: 'GET',
            ...options
        };
        const newUrl = newOptions.method == 'GET' ? this.makeQueryString(url, options.data) : url;

        return new Promise((resolve, reject) => {
            fetch(newUrl, newOptions)
            .then(response => {
                if (!response.ok) {
                    // For server errors
                    const xReason = response.headers.get('X-Reason');
                    if(xReason) return reject(xReason);
                    return response.text().then(text => reject(parseResponse(text)));
                } else {
                    return response.text();
                }
            })
            .then(text => {
                resolve(parseResponse(text));
            })
            .catch(err => {
                // For network errors
                reject(err);
            })
        });

        function parseResponse(text) {
            try {
                return JSON.parse(text);
            } catch(e) {
                return text;
            }
        }

    }
}
// Automatically cache method results. 
Object.getOwnPropertyNames(apiDefinition.prototype).forEach(prop => {
    const skip = ['constructor', 'fetch', 'makeQueryString', 'getURL', 'getRSSURL', 'getCalendarICS', 'getEventICS', 'triggerICSDownload', 'setPageTitle', 'setPageDescription', 'setPageCanonical', 'hexToRGB', 'adjustScrollPosition', 'submitRSVP', 'submitEvent', 'verifyPasscode'];   // Don't try to cache these methods

    if (typeof apiDefinition.prototype[prop] == 'function' && !skip.includes(prop) ) {
        const originalMethod = apiDefinition.prototype[prop];

        apiDefinition.prototype[prop] = function() {
            const cacheKey = JSON.stringify({
                method: prop,
                arguments: [...arguments]
            });
            const cache = this.cache.check(cacheKey);
            if (!arguments[1]?.force && cache) {
                return new Promise((resolve, reject) => {
                    resolve(cache);     // return cached results
                });
            }

            return originalMethod.apply(this, arguments).then(data => {
                this.cache.save(cacheKey, data);   // cache results
                return data;    // pass data back chained then()
            })
        };
    }
})


class Cache {
    constructor(staleTime) {
        this.staleTime = staleTime || 180000; // 3 mins in milliseconds
        this.cache = new Map();
    }

    /**
     * Checks if the key exists in the cache and is still fresh
     * @param {String} key 
     * @returns {*} - Returns cache data if key exists and is fresh and false otherwise
     */
    check(key) {
        const cache = this.cache.get(key);
        if (cache && (cache.cacheTime + this.staleTime > Date.now())) {
            // retrieve from cache
            return cache.data;
        } else {
            // refetch
            return false;
        }
    }

    /**
     * Saves data to the cache
     * @param {String} key 
     * @param {*} data 
     */
    save(key, data) {
        this.cache.set(key, {
            data,
            cacheTime: Date.now()
        });
    }
}


const api = new apiDefinition();
export {
    api,
};